import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowUpRight, 
  BarChart3, 
  ShoppingCart, 
  Target, 
  TrendingUp, 
  Check,
  Zap,
  Globe,
  LineChart,
  Users
} from 'lucide-react';

const HomePage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: 'Consulenza Marketplace',
      description: 'Scala la tua presenza su eBay, Amazon e altri marketplace con strategie collaudate.',
      link: '/services#marketplace'
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Consulenza Ecommerce',
      description: 'Ottimizza il tuo negozio Shopify e le operazioni ecommerce per massimizzare le conversioni.',
      link: '/services#ecommerce'
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Digital Marketing',
      description: 'Accelera la crescita con strategie di performance marketing su Meta, TikTok e altre piattaforme.',
      link: '/services#marketing'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Crescita Aziendale',
      description: 'Pianificazione strategica ed esecuzione per scalare ricavi e operazioni.',
      link: '/services#growth'
    }
  ];

  const whyChooseUs = [
    {
      icon: <BarChart3 className="w-5 h-5" />,
      title: 'Approccio Data-Driven',
      description: 'Ogni strategia è supportata da analytics e KPI misurabili.'
    },
    {
      icon: <Zap className="w-5 h-5" />,
      title: 'Eccellenza nell\'Esecuzione',
      description: 'Non ci limitiamo a pianificare — eseguiamo e consegniamo risultati.'
    },
    {
      icon: <LineChart className="w-5 h-5" />,
      title: 'Sistemi Scalabili',
      description: 'Costruiamo processi che crescono con il tuo business.'
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: 'Mentalità di Partnership',
      description: 'Lavoriamo come un\'estensione del tuo team.'
    }
  ];

  const clientTypes = [
    'Brand Ecommerce',
    'Venditori Marketplace',
    'Aziende DTC',
    'Startup & Scale-up',
    'Aziende B2B',
    'Attività Retail'
  ];

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#2DD4BF]/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-[#2DD4BF]/3 rounded-full blur-[80px]" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <span className="inline-block text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-6">
                Ecommerce specialist
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1D24] leading-tight tracking-tight mb-6">
                Scala il Tuo Ecommerce e Business Digitale con{' '}
                <span className="text-gradient">Consulenza Strategica</span>
              </h1>
              <p className="text-lg text-[#4B5563] leading-relaxed max-w-xl mb-10">
                Aiutiamo le aziende a crescere sui marketplace, ecommerce e canali digitali attraverso strategie data-driven e un'esecuzione impeccabile.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-book"
                  className="inline-flex items-center gap-2 bg-[#2DD4BF] text-white px-8 py-4 font-semibold rounded-sm hover:bg-[#14B8A6] transition-all hover:scale-105 shadow-lg"
                >
                  Prenota una Consulenza
                  <ArrowUpRight size={20} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-services"
                  className="inline-flex items-center gap-2 bg-white border border-[#E5E7EB] text-[#1A1D24] px-8 py-4 font-semibold rounded-sm hover:bg-[#F9FAFB] transition-all shadow-sm"
                >
                  Scopri i Servizi
                </Link>
              </div>
            </motion.div>

            {/* Right Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-5 hidden lg:block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#2DD4BF]/20 to-transparent rounded-lg" />
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=700&fit=crop"
                  alt="Analytics di Crescita Digitale"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
                {/* Floating Stats Card */}
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-xl border border-[#E5E7EB]">
                  <div className="text-3xl font-bold text-[#2DD4BF] mb-1">+240%</div>
                  <div className="text-[#6B7280] text-sm">Tasso di Crescita Medio</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
              I Nostri Servizi
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1D24] tracking-tight">
              Come Ti Aiutiamo a Crescere
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={service.link}
                  data-testid={`service-card-${index}`}
                  className="block bg-[#F9FAFB] border border-[#E5E7EB] p-8 rounded-lg hover:border-[#2DD4BF]/50 hover:shadow-lg transition-all group"
                >
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-lg bg-[#2DD4BF]/10 flex items-center justify-center text-[#2DD4BF] mb-6 group-hover:bg-[#2DD4BF]/20 transition-colors">
                      {service.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-[#1A1D24] mb-3 group-hover:text-[#2DD4BF] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-[#6B7280] leading-relaxed">
                      {service.description}
                    </p>
                    <div className="mt-6 flex items-center gap-2 text-[#2DD4BF] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Scopri di più <ArrowUpRight size={16} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 md:py-32 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
                Perché Sceglierci
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1D24] tracking-tight mb-6">
                Consulenza Orientata ai Risultati per la Crescita Digitale
              </h2>
              <p className="text-[#6B7280] leading-relaxed mb-8">
                Combiniamo pensiero strategico con esecuzione pratica per consegnare risultati misurabili al tuo business. Il nostro approccio si basa su dati, esperienza e impegno per il tuo successo.
              </p>
              <div className="space-y-6">
                {whyChooseUs.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex gap-4 border-l-2 border-[#E5E7EB] pl-6 hover:border-[#2DD4BF] transition-colors"
                  >
                    <div className="text-[#2DD4BF] mt-1">{item.icon}</div>
                    <div>
                      <h4 className="text-[#1A1D24] font-semibold mb-1">{item.title}</h4>
                      <p className="text-[#6B7280] text-sm">{item.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-white border border-[#E5E7EB] p-10 rounded-lg shadow-lg">
                <div className="text-6xl font-bold text-gradient mb-2">10+</div>
                <p className="text-[#6B7280] mb-8">Anni di Esperienza Combinata</p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-3xl font-bold text-[#1A1D24]">50+</div>
                    <p className="text-[#6B7280] text-sm">Clienti Serviti</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#1A1D24]">€5M+</div>
                    <p className="text-[#6B7280] text-sm">Ricavi Generati</p>
                  </div>
                </div>
              </div>
              {/* Decorative Element */}
              <div className="absolute -z-10 top-6 left-6 w-full h-full border-2 border-[#2DD4BF]/20 rounded-lg" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Client Types Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
              Chi Serviamo
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1D24] tracking-tight">
              Costruito per Aziende Orientate alla Crescita
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center gap-4"
          >
            {clientTypes.map((type, index) => (
              <motion.div
                key={type}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="flex items-center gap-3 bg-[#F9FAFB] border border-[#E5E7EB] px-6 py-4 rounded-lg hover:border-[#2DD4BF]/30 hover:shadow-md transition-all"
              >
                <Check className="w-4 h-4 text-[#2DD4BF]" />
                <span className="text-[#4B5563] font-medium">{type}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-gradient-to-br from-[#F4F5F7] to-[#E8E9EB]">
        <div className="absolute inset-0 bg-gradient-to-r from-[#2DD4BF]/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1D24] tracking-tight mb-6">
              Pronto a Scalare il Tuo Business?
            </h2>
            <p className="text-[#6B7280] text-lg max-w-xl mx-auto mb-10">
              Parliamo dei tuoi obiettivi di crescita e creiamo una strategia per raggiungerli.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="cta-book-consultation"
                className="inline-flex items-center gap-2 bg-[#2DD4BF] text-white px-8 py-4 font-semibold rounded-sm hover:bg-[#14B8A6] transition-all hover:scale-105 shadow-lg"
              >
                Prenota una Consulenza
                <ArrowUpRight size={20} />
              </Link>
              <Link
                to="/about"
                data-testid="cta-learn-more"
                className="inline-flex items-center gap-2 bg-white border border-[#E5E7EB] text-[#1A1D24] px-8 py-4 font-semibold rounded-sm hover:bg-[#F9FAFB] transition-all shadow-sm"
              >
                Scopri Chi Siamo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
